import JSZip from "jszip";
import * as basket from "../../eaiws/basket";
import { Log } from "../../utils";
import { clone, contains } from "../../utils/array";
import { forEachParallel } from "../../utils/async";
import { ERR_INVALID_ARGUMENT, newError } from "../../utils/error";
import { isNullOrEmpty } from "../../utils/string";
import { ArticleItem } from "../ArticleItem";
import { FolderItem } from "../FolderItem";
import { ItemUpdateFlags } from "../BaseItem";
import { MAX_CONCURRENT_ARTICLE_UPDATES } from "../Constants";
import { SetArticleItem } from "../SetArticleItem";
import { UserArticleItem } from "../UserArticleItem";
export async function insertArticle(pBasket, pArticle, pOptions) {
    if (pArticle == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    let tInsertInfo;
    if (pArticle instanceof basket.InsertInfo) {
        tInsertInfo = pArticle;
    }
    else {
        tInsertInfo = new basket.InsertInfo();
        tInsertInfo.articlePackageId = pArticle.articlePackageId;
        tInsertInfo.baseArticleNumber = pArticle.baseArticleNumber;
        tInsertInfo.catalogId = pArticle.catalogId;
        tInsertInfo.catalogPackageId = pArticle.catalogPackageId;
        if (pArticle.varCodeType === "OFML")
            tInsertInfo.ofmlVariantCode = pArticle.variantCode;
        else
            tInsertInfo.variantCode = pArticle.variantCode;
    }
    const tInsertPos = getInsertPosition(pBasket);
    const tId = await pBasket.session.basket.insertOFMLArticle(tInsertPos.parent.id, tInsertPos.beforeItemId, tInsertInfo, pOptions);
    if (isNullOrEmpty(tId))
        throw new Error("Failed to insert article " + pArticle.baseArticleNumber);
    await updateParentsAfterInsertOrRemove(pBasket, [tInsertPos.parent], [tId], true);
    const tArticle = pBasket.getItemById(tId);
    if (tArticle == null)
        throw new Error("Failed to insert article " + pArticle.baseArticleNumber);
    return (tArticle);
}
export async function insertContainerItem(pBasket, pContainer) {
    var _a;
    if (pContainer.type !== "Container")
        throw new Error("Invalid catalog item type.");
    //get container uri
    let tUri = null;
    if (pContainer.resources != null) {
        for (const tRes of pContainer.resources) {
            if (tRes.type === "XCF:PEC") {
                tUri = (_a = tRes.url) !== null && _a !== void 0 ? _a : tRes.value;
                break;
            }
        }
    }
    if (isNullOrEmpty(tUri))
        throw new Error("Catalog item does not have XCF:PEC resource.");
    const tInsertPos = getInsertPosition(pBasket);
    const tOptions = new basket.PasteContainerOptions();
    tOptions.setArticleIds = true;
    tOptions.geometryIds = true;
    tOptions.ignoreComposableGeometries = true;
    const tPasteResult = await pBasket.session.basket.pasteContainer(tInsertPos.parent.id, tInsertPos.beforeItemId, tUri, tOptions);
    if (tPasteResult == null)
        throw new Error("Failed to insert container.");
    try {
        pBasket._beginItemUpdate();
        pBasket._beginCaching();
        //add items to cache
        for (const tItem of tPasteResult.items)
            pBasket._addEaiwsItemToCache(tItem);
        await updateParentsAfterInsertOrRemove(pBasket, [tInsertPos.parent]);
        const tItems = new Array();
        for (const tPastedItem of tPasteResult.items) {
            const tItem = pBasket.getItemById(tPastedItem.itemId);
            if (tItem != null)
                tItems.push(tItem);
        }
        return (tItems);
    }
    finally {
        pBasket._endCaching();
        pBasket._endItemUpdate();
    }
}
export async function insertFolder(pBasket, pName, pOptions) {
    const tOptions = {
        showSubTotal: false
    };
    Object.assign(tOptions, pOptions);
    const tInsertPos = getInsertPosition(pBasket);
    const tId = await pBasket.session.basket.insertFolder(tInsertPos.parent.id, tInsertPos.beforeItemId, pName);
    if (tOptions.showSubTotal) {
        const tProps = new basket.ItemProperties();
        tProps.folder = new basket.FolderProperties();
        tProps.folder.showSubTotal = true;
        await pBasket.session.basket.setItemProperties(tId, tProps);
    }
    if (isNullOrEmpty(tId))
        throw new Error("Failed to insert folder.");
    await updateParentsAfterInsertOrRemove(pBasket, [tInsertPos.parent], [tId]);
    const tFolder = pBasket.getItemById(tId);
    if (tFolder == null)
        throw new Error("Failed to insert folder.");
    return (tFolder);
}
export async function insertSetArticle(pBasket, pName) {
    const tInsertPos = getInsertPosition(pBasket, null, null, true);
    let tId = await pBasket.session.basket.insertFolder(tInsertPos.parent.id, tInsertPos.beforeItemId, pName);
    if (isNullOrEmpty(tId))
        throw new Error("Failed to insert set article.");
    tId = await pBasket.session.basket.convertToSetArticle(tId);
    await updateParentsAfterInsertOrRemove(pBasket, [tInsertPos.parent], [tId]);
    const tSet = pBasket.getItemById(tId);
    if (tSet == null)
        throw new Error("Failed to insert set article.");
    return (tSet);
}
export async function insertUserArticle(pBasket, pShortText) {
    const tInsertPos = getInsertPosition(pBasket);
    const tId = await pBasket.session.basket.insertUserArticle(tInsertPos.parent.id, tInsertPos.beforeItemId, pShortText);
    if (isNullOrEmpty(tId))
        throw new Error("Failed to insert user article.");
    await updateParentsAfterInsertOrRemove(pBasket, [tInsertPos.parent], [tId]);
    const tText = pBasket.getItemById(tId);
    if (tText == null)
        throw new Error("Failed to insert user article.");
    return (tText);
}
export async function insertTextItem(pBasket, pText) {
    const tInsertPos = getInsertPosition(pBasket);
    const tId = await pBasket.session.basket.insertTextItem(tInsertPos.parent.id, tInsertPos.beforeItemId, pText);
    if (isNullOrEmpty(tId))
        throw new Error("Failed to insert text item.");
    await updateParentsAfterInsertOrRemove(pBasket, [tInsertPos.parent], [tId]);
    const tText = pBasket.getItemById(tId);
    if (tText == null)
        throw new Error("Failed to insert text item.");
    return (tText);
}
export async function deleteItems(pBasket, pItems, pOptions) {
    if (pItems.length === 0)
        return;
    const tOptions = {
        wholeComposite: true,
        children: true
    };
    Object.assign(tOptions, pOptions);
    const tParents = getParentsToUpdateAfterRemove(pItems, tOptions.wholeComposite, tOptions.children);
    const tDeleteOptions = new basket.DeleteItemsOptions();
    tDeleteOptions.wholeComposite = tOptions.wholeComposite;
    tDeleteOptions.subItems = tOptions.children;
    const tItemIds = new Array();
    for (const tItem of pItems)
        tItemIds.push(tItem.id);
    await pBasket.session.basket.deleteItems(tItemIds, tDeleteOptions);
    await updateParentsAfterInsertOrRemove(pBasket, tParents);
}
/**
 * Copies the given items into an OBX file. Returns the url of the OBX file.
 */
export async function copyItems(pBasket, pItems, pOptions) {
    const tOptions = {
        wholeComposite: true,
        children: true,
        cut: false
    };
    Object.assign(tOptions, pOptions);
    const tOpMode = new basket.OperationMode();
    tOpMode.mainArticles = tOptions.wholeComposite;
    tOpMode.subPositions = tOptions.children;
    const tCopyOptions = new basket.CopyOptions();
    tCopyOptions.cut = tOptions.cut;
    let tParents;
    if (tOptions.cut)
        tParents = getParentsToUpdateAfterRemove(pItems, tOptions.wholeComposite, tOptions.children);
    const tItemIds = new Array();
    for (const tItem of pItems)
        tItemIds.push(tItem.id);
    const tUrl = await pBasket.session.basket.copy(tItemIds, tOpMode, null, tCopyOptions);
    if (tOptions.cut)
        await updateParentsAfterInsertOrRemove(pBasket, tParents);
    return (tUrl);
}
/**
 * Inserts the items which are part of the given OBX. Only eaiws session urls are accepted here.
 * Use importObx() for external files.
 */
export async function pasteItems(pBasket, pObxUrl, pOptions) {
    const getPastedItems = (pIds) => {
        const tItems = new Array();
        for (const tId of pIds) {
            const tItem = pBasket.getItemById(tId);
            if (tItem != null) {
                tItems.push(tItem);
            }
        }
        return (tItems);
    };
    const tOptions = {
        parent: null,
        beforeItem: null
    };
    Object.assign(tOptions, pOptions);
    const tPasteOptions = new basket.PasteOptions();
    tPasteOptions.returnOriginalItemIds = true;
    const tInsertPos = getInsertPosition(pBasket, tOptions.parent, tOptions.beforeItem);
    const tPasteResult = await pBasket.session.basket.paste(tInsertPos.parent.id, tInsertPos.beforeItemId, pObxUrl, tPasteOptions);
    //map from pasted to original id
    const tOrgIdMap = new Map();
    const tPastedIds = new Array();
    for (let i = 0; i < tPasteResult.length - 1; i += 2) {
        const tOrgId = tPasteResult[i + 1];
        const tPastedId = tPasteResult[i];
        if (tOrgId !== tPastedId) { //only if ids are unequal
            tOrgIdMap.set(tPastedId, tOrgId);
        }
        tPastedIds.push(tPastedId);
    }
    //update parent and create new items
    await updateParentsAfterInsertOrRemove(pBasket, [tInsertPos.parent], tPastedIds);
    let tPastedItems = getPastedItems(tPastedIds);
    //set handling
    const tSetParent = tInsertPos.parent instanceof SetArticleItem ?
        tInsertPos.parent : tInsertPos.parent.getSetArticle();
    if (tSetParent != null) {
        await addItemsToCurrentSet(pBasket, tPastedItems);
        await updateParentsAfterInsertOrRemove(pBasket, [tSetParent]);
        //get pasted items again, some items like set articles may be converted during the operation
        tPastedItems = getPastedItems(tPastedIds);
    }
    //trigger paste event
    if (pBasket.eventItemsPasted.hasListeners) {
        const tEventData = new Array();
        for (const tPastedItem of tPastedItems) {
            let tOrgItem = null;
            const tOrgId = tOrgIdMap.get(tPastedItem.id);
            if (tOrgId != null) {
                tOrgItem = pBasket.getItemById(tOrgId);
            }
            tEventData.push({
                originalItem: tOrgItem,
                item: tPastedItem
            });
        }
        pBasket.eventItemsPasted.trigger(tEventData);
    }
    return (tPastedItems);
}
export async function relocateItems(pBasket, pItems, pNewParent, pBeforeItem, pOptions) {
    if (pItems.length === 0)
        return ([]);
    const tOptions = {
        reorder: false,
        copy: false
    };
    Object.assign(tOptions, pOptions);
    if (tOptions.copy) {
        //use copy paste
        const tUrl = await copyItems(pBasket, pItems);
        const tPastedItems = await pasteItems(pBasket, tUrl, { parent: pNewParent, beforeItem: pBeforeItem });
        //delete obx after paste
        await pBasket.session.deleteFile(tUrl);
        return (tPastedItems);
    }
    else {
        //use relocateItems
        const tItems = tOptions.reorder ? pItems : sortItems(pItems);
        const tItemIds = tItems.map((pItem) => pItem.id);
        const tParents = getParentsToUpdateAfterRemove(pItems, false, true, true, [pNewParent]);
        //is new parent a set (or part of a set)?
        const tSetParent = pNewParent instanceof SetArticleItem ? pNewParent : pNewParent.getSetArticle();
        if (tSetParent != null) {
            if (!contains(tParents, tSetParent)) {
                tParents.push(tSetParent);
            }
        }
        //remove all moved items from their current set
        await removeItemsFromCurrentSet(pBasket, pItems, tSetParent == null);
        const tResult = await pBasket.session.basket.relocateItems(pNewParent.id, pBeforeItem != null ? pBeforeItem.id : "", tItemIds);
        if (tResult === "Done") {
            if (tSetParent != null) {
                await addItemsToCurrentSet(pBasket, pItems);
            }
            await updateParentsAfterInsertOrRemove(pBasket, tParents);
        }
        else {
            if (tResult !== "NothingToDo")
                throw newError("Failed to relocate items: " + tResult);
        }
        return (clone(pItems));
    }
}
/**
 * Moves the given items in the desired direction if possible.
 * `getPossibleMoveDirections()` should be used in advance to check if a movement is possible.
 */
export async function moveItems(pBasket, pItems, pDirection) {
    if (pItems.length === 0)
        return ("NothingToDo");
    const tItems = sortItems(pItems);
    const tFirstItem = tItems[0];
    let tNewParent = null;
    let tSetParent = null;
    const tCurrentParent = tFirstItem.parent;
    if (tCurrentParent == null)
        throw newError("Failed to move items: current parent not found");
    if (pDirection === "Unindent" || pDirection === "Indent") {
        if (pDirection === "Unindent") {
            tNewParent = tCurrentParent.parent;
            if (tNewParent == null) {
                return ("NoGrandparent");
            }
        }
        else {
            tNewParent = tCurrentParent.getPrevChild(tFirstItem);
            if (tNewParent == null) {
                return ("NoPredecessor");
            }
        }
        //is new parent a set (or part of a set)?
        tSetParent = tNewParent instanceof SetArticleItem ? tNewParent : tNewParent.getSetArticle();
        //remove all moved items from their current set
        await removeItemsFromCurrentSet(pBasket, tItems, tSetParent == null);
    }
    //move the items
    const tItemIds = tItems.map((pItem) => pItem.id);
    const tResult = await pBasket.session.basket.moveItems(tItemIds, pDirection);
    //update parent items
    if (tResult === "Done") {
        if (tNewParent == null) {
            await tCurrentParent.updateItem(ItemUpdateFlags.Item);
        }
        else {
            const tParents = [tCurrentParent, tNewParent];
            //set handling
            if (tSetParent != null) {
                if (tNewParent !== tSetParent && tCurrentParent !== tSetParent) {
                    tParents.push(tSetParent);
                }
                await addItemsToCurrentSet(pBasket, tItems);
            }
            await updateParentsAfterInsertOrRemove(pBasket, tParents);
        }
    }
    return (tResult);
}
/**
* Returns all currently possible move directions for the given items.
*/
export function getPossibleMoveDirections(pBasket, pItems, pLockManager) {
    const tDirections = new Set();
    if (pItems.length === 0)
        return (tDirections);
    //ceck that all items have the same parent
    const tParent = pItems[0].parent;
    if (tParent == null)
        return (tDirections);
    for (let i = 1; i < pItems.length; ++i) {
        if (pItems[i].parent !== tParent)
            return (tDirections);
    }
    const tItems = sortItems(pItems);
    const tFirstItem = tItems[0];
    const tLastItem = tItems[tItems.length - 1];
    //check if the sequence is consecutive
    let tLastIndex = tItems[0].childIndex;
    for (let i = 1; i < tItems.length; ++i) {
        const tCurrentIndex = tItems[i].childIndex;
        if ((tCurrentIndex - tLastIndex) !== 1)
            return (tDirections);
        tLastIndex = tCurrentIndex;
    }
    let tCalcIsLocked = false;
    if (pLockManager != null) {
        tCalcIsLocked = pLockManager.isCalculationLocked(tItems);
    }
    //move up
    if (tFirstItem.childIndex > 0) {
        tDirections.add("Up");
    }
    //move down
    if (tLastItem.childIndex < (tParent.children.length - 1)) {
        tDirections.add("Down");
    }
    //outdent
    if (tParent.parent != null && tLastItem.childIndex === (tParent.children.length - 1)) {
        let tMayUnindent = true;
        if (tCalcIsLocked) {
            //if calculation is locked it is not allowed to move the locked item into a new set or out of the current set
            const tNewSet = tParent.parent instanceof SetArticleItem ? tParent.parent : tParent.parent.getSetArticle();
            for (const tItem of tItems) {
                if (tNewSet != tItem.getSetArticle()) {
                    tMayUnindent = false;
                    break;
                }
            }
        }
        if (tMayUnindent) {
            tDirections.add("Unindent");
        }
    }
    //indent
    const tPrevChild = tParent.getPrevChild(tFirstItem);
    if (tPrevChild != null && tPrevChild.type !== "Text") {
        //a parent of folder / set has to be a folder / set(a group)
        let tGroupSelected = false;
        for (const tItem of tItems) {
            if (tItem.isGroup) {
                tGroupSelected = true;
                break;
            }
        }
        let tMayIndent = !tGroupSelected || tPrevChild.isGroup;
        if (tMayIndent && tCalcIsLocked) {
            //if calculation is locked it is not allowed to move the locked item into a new set or out of the current set
            const tNewSet = tPrevChild instanceof SetArticleItem ? tPrevChild : tPrevChild.getSetArticle();
            for (const tItem of tItems) {
                if (tNewSet != tItem.getSetArticle()) {
                    tMayIndent = false;
                    break;
                }
            }
        }
        if (tMayIndent) {
            tDirections.add("Indent");
        }
    }
    return (tDirections);
}
/** @deprecated  Use `setItemInactiveState` instead. */
export async function setItemAlternativeState(pBasket, pItems, pAlternative) {
    return (setItemInactiveState(pBasket, pItems, pAlternative ? "Alternative" : "None"));
}
export async function setItemInactiveState(pBasket, pItems, pNewState) {
    if (pItems.length === 0)
        return;
    try {
        pBasket._beginItemUpdate();
        let tAltPos = false;
        let tOptional;
        if (pNewState !== "None") {
            tAltPos = true;
            tOptional = pNewState === "Optional";
        }
        const collectMainArticles = (pParent) => {
            for (const tItem of pParent.children) {
                if (tItem instanceof ArticleItem) {
                    const tMainArticle = tItem.getMainArticle();
                    tMainArticles.add(tMainArticle);
                }
                collectMainArticles(tItem);
            }
        };
        //get folder and articles, they need to be handled differently
        const tMainArticles = new Set();
        const tFolders = new Array();
        for (const tItem of pItems) {
            if (tItem instanceof ArticleItem) {
                const tMainArticle = tItem.getMainArticle();
                tMainArticles.add(tMainArticle);
            }
            else if (tItem instanceof FolderItem) {
                tFolders.push(tItem);
            }
        }
        if (tFolders.length === 0 && tMainArticles.size === 0)
            return;
        const tOptions = new basket.ItemSelectionOptions();
        tOptions.wholeComposite = true;
        //change state for main articles (and sub articles)
        if (tMainArticles.size > 0) {
            const tItemArray = Array.from(tMainArticles);
            const tItemIds = tItemArray.map((pItem) => pItem.id);
            await pBasket.session.basket.changeAlternativePositionState(tItemIds, "", tAltPos, tOptions, tOptional);
        }
        //change state for all article childs of the folders
        if (tFolders.length > 0) {
            tOptions.subItems = true;
            const tItemIds = tFolders.map((pItem) => pItem.id);
            await pBasket.session.basket.changeAlternativePositionState(tItemIds, "", tAltPos, tOptions, tOptional);
            //get all main article from the folders, to updade them
            for (const tFolder of tFolders)
                collectMainArticles(tFolder);
        }
        //update collected main articles
        const tPromises = new Array();
        tMainArticles.forEach((pArticle) => {
            tPromises.push(pArticle.updateWholeComposite(ItemUpdateFlags.ItemProps));
            pArticle.invalidateGroupCalculations();
        });
        await Promise.all(tPromises);
    }
    finally {
        pBasket._endItemUpdate();
    }
}
export async function convertToUserArticles(pBasket, pItems) {
    if (pItems.length === 0)
        return ([]);
    try {
        pBasket._beginItemUpdate();
        //collect main articles to convert
        const tMainArticles = new Set();
        for (const tItem of pItems) {
            if (tItem instanceof ArticleItem && !(tItem instanceof UserArticleItem) && !(tItem instanceof SetArticleItem)) {
                const tMainArticle = tItem.getMainArticle();
                tMainArticles.add(tMainArticle);
            }
        }
        if (tMainArticles.size === 0)
            return ([]);
        const tItemArray = Array.from(tMainArticles);
        const tItemIds = tItemArray.map((pItem) => pItem.id);
        const tParents = getParentsToUpdateAfterRemove(tItemArray, true, false);
        const tOptions = new basket.ItemSelectionOptions();
        tOptions.wholeComposite = true;
        const tConvItemIds = await pBasket.session.basket.convertToUserArticles(tItemIds, tOptions);
        await updateParentsAfterInsertOrRemove(pBasket, tParents, tConvItemIds);
        const tConvItems = new Array();
        for (const tId of tConvItemIds) {
            const tConvItem = pBasket.getItemById(tId);
            if (tConvItem != null && tConvItem instanceof UserArticleItem)
                tConvItems.push(tConvItem);
        }
        return (tConvItems);
    }
    finally {
        pBasket._endItemUpdate();
    }
}
export async function convertToSetArticle(pBasket, pFolder) {
    try {
        pBasket._beginItemUpdate();
        pBasket._beginCaching();
        const tOpMode = new basket.OperationMode();
        tOpMode.breakUpSetArticles = true;
        const tParent = pFolder.parent == null ? pBasket.root : pFolder.parent;
        const tSetId = await pBasket.session.basket.convertToSetArticle(pFolder.id, tOpMode);
        if (isNullOrEmpty(tSetId))
            throw new Error("Failed to convert to set article.");
        await pBasket._fetchEaiwsItems([tSetId], true, false);
        //update parent
        await updateParentsAfterInsertOrRemove(pBasket, [tParent], [tSetId]);
        const tSet = pBasket.getItemById(tSetId);
        if (tSet == null)
            throw new Error("Failed to convert to set article.");
        //update all children (update subarticle id and create new items (breakUpSetArticles))
        await tSet.updateItem(ItemUpdateFlags.Item | ItemUpdateFlags.IncludeChildren);
        return (tSet);
    }
    finally {
        pBasket._endCaching();
        pBasket._endItemUpdate();
    }
}
export async function breakUpSetArticle(pBasket, pSetArticle) {
    try {
        pBasket._beginItemUpdate();
        pBasket._beginCaching();
        const tParent = pSetArticle.parent == null ? pBasket.root : pSetArticle.parent;
        const tFolderId = await pBasket.session.basket.breakUpSetArticle(pSetArticle.id);
        if (isNullOrEmpty(tFolderId))
            throw new Error("Failed to break up set article.");
        //was the set a nested set?
        //If yes add the folder and its children to the parent set
        const tSetParent = tParent instanceof SetArticleItem ? tParent : tParent.getSetArticle();
        if (tSetParent != null) {
            //Note: OperationMode.subPositions is not supported from addToSetArticle
            const tItemsToAdd = collectAllItems(pSetArticle.children); //get children of the original set which are now the children of the new folder
            const tItemsToAddIds = tItemsToAdd.map((pItem) => pItem.id);
            tItemsToAddIds.push(tFolderId); //also add the folder
            await pBasket.session.basket.addToSetArticle(tItemsToAddIds);
        }
        await pBasket._fetchEaiwsItems([tFolderId], true, false);
        //update parent
        await updateParentsAfterInsertOrRemove(pBasket, [tParent], [tFolderId]);
        const tFolder = pBasket.getItemById(tFolderId);
        if (tFolder == null)
            throw new Error("Failed to break up set article.");
        //update setArticleId of all former set parts
        await tFolder.updateItem(ItemUpdateFlags.Item | ItemUpdateFlags.IncludeChildren);
        return (tFolder);
    }
    finally {
        pBasket._endCaching();
        pBasket._endItemUpdate();
    }
}
/**
 * Tries to merge the given articles. Returns true if articles were merged.
 */
export async function mergeArticles(pBasket, pItems, pOptions) {
    const tOptions = {
        folderMode: "Ignore",
    };
    Object.assign(tOptions, pOptions);
    //collect main, user articles and folders
    const tFolderIds = new Set();
    const tArticleIds = new Set();
    for (const tItem of pItems) {
        if (tItem.isOfmlArticle || tItem instanceof UserArticleItem) {
            tArticleIds.add(tItem.getMainArticle().id);
        }
        if (tItem instanceof FolderItem) {
            tFolderIds.add(tItem.id);
        }
    }
    //delete folders
    if (tFolderIds.size > 0 && tOptions.folderMode === "Delete") {
        const tDeleteOptions = new basket.DeleteItemsOptions();
        tDeleteOptions.subItems = false;
        await pBasket.session.basket.deleteItems(Array.from(tFolderIds), tDeleteOptions);
    }
    //merge
    const tSelOptions = new basket.ItemSelectionOptions();
    tSelOptions.wholeComposite = true;
    const tItemIds = Array.from(tArticleIds);
    if (tOptions.folderMode === "MergeChildren") {
        tSelOptions.subItems = true;
        tItemIds.push(...Array.from(tFolderIds));
    }
    let tResult;
    //eaiws merges whole basket if array is empty
    if (tItemIds.length > 0) {
        tResult = await pBasket.session.basket.mergeBasketArticles(tItemIds, tSelOptions);
    }
    else {
        tResult = {};
    }
    //update items
    if (tOptions.folderMode === "Delete" ||
        (tResult.addedItems != null && tResult.addedItems.length > 0) ||
        (tResult.removedItems != null && tResult.removedItems.length > 0) ||
        (tResult.movedItems != null && tResult.movedItems.length > 0) ||
        (tResult.updatedItems != null && tResult.updatedItems.length > 0)) {
        try {
            pBasket._beginItemUpdate();
            pBasket._beginCaching();
            //TODO: this could be optimized, do not update whole tree
            await pBasket._fetchEaiwsItems([pBasket.root.id], true, true);
            await pBasket.root.updateItem(ItemUpdateFlags.Item | ItemUpdateFlags.IncludeChildren);
            //update item props and calculation of changed items
            if (tResult.updatedItems != null) {
                const tPromises = new Array();
                for (const tItemId of tResult.updatedItems) {
                    const tItem = pBasket.getItemById(tItemId);
                    if (tItem instanceof ArticleItem) {
                        tPromises.push(tItem.updateWholeComposite(ItemUpdateFlags.ItemProps | ItemUpdateFlags.Calculation));
                    }
                }
                await Promise.all(tPromises);
            }
            return (true);
        }
        finally {
            pBasket._endCaching();
            pBasket._endItemUpdate();
        }
    }
    else {
        return (false);
    }
}
/**
 * Updates or migrates the given article items.
 * If the optional `pProgressCallback` returns false, the process will be aborted.
 * Errors during article update will be collected and returned as part of the result. They are not thrown as an exception.
 */
export async function updateOfmlArticles(pBasket, pItems, pMigrate, pProgressCallback, pPriceDate) {
    //collect main articles
    const tMainArticles = new Set();
    for (const tItem of pItems) {
        if (tItem.isOfmlArticle) {
            tMainArticles.add(tItem.getMainArticle());
        }
    }
    //sort articles by state
    const tInvalidArticles = new Array();
    const tMigratableArticles = new Array();
    const tUpdateArticles = new Array();
    tMainArticles.forEach((pArticle) => {
        switch (pArticle.ofmlUpdateState) {
            case "Unknown":
            case "Updatable":
                tUpdateArticles.push(pArticle);
                break;
            case "Migratable":
                if (pMigrate) {
                    tUpdateArticles.push(pArticle);
                }
                else {
                    tMigratableArticles.push(pArticle);
                }
                break;
            case "UpToDate":
                //in this case only if new price date needs to be used
                if (pPriceDate != null) {
                    tUpdateArticles.push(pArticle);
                }
                break;
            default:
                tInvalidArticles.push(pArticle);
        }
    });
    const tResult = {
        updated: 0,
        migratable: tMigratableArticles.length,
        invalid: tInvalidArticles.length,
        errors: new Array()
    };
    if (tUpdateArticles.length === 0)
        return (tResult);
    //update ofml articles
    const tOptions = new basket.UpdateBasketArticlesOptions();
    tOptions.migrate = pMigrate;
    tOptions.update = true;
    tOptions.priceDate = pPriceDate;
    const tUpdateIds = tUpdateArticles.map((pItem) => pItem.id);
    const tResults = new Array();
    let tFinishedUpdates = 0;
    let tAborted = false;
    if (pProgressCallback != null) {
        tAborted = !pProgressCallback(tFinishedUpdates, tUpdateArticles.length);
    }
    await forEachParallel(tUpdateArticles, async (pIndex, pArticle) => {
        if (tAborted) {
            return;
        }
        try {
            const tResult = await pBasket.session.basket.updateBasketArticles([pArticle.id], null, tOptions);
            tResults.push(...tResult);
        }
        catch (pError) {
            const tUpdateResult = new basket.UpdateBasketArticleResult();
            tUpdateResult.itemId = pArticle.id;
            if (pMigrate) {
                //migration failed => handle this article as invalid and return the error
                tUpdateResult.updateState = "Invalid";
                tResult.errors.push({
                    item: pArticle,
                    error: pError
                });
            }
            else {
                //update failed => handle this article as migratable, maybe migration will work
                tUpdateResult.updateState = "Migratable";
            }
            tResults.push(tUpdateResult);
        }
        tFinishedUpdates++;
        if (pProgressCallback != null) {
            tAborted = tAborted || !pProgressCallback(tFinishedUpdates, tUpdateArticles.length);
        }
    }, MAX_CONCURRENT_ARTICLE_UPDATES);
    //update items
    pBasket._beginItemUpdate();
    pBasket._beginCaching();
    try {
        const tResultMap = new Map();
        for (const tRes of tResults)
            tResultMap.set(tRes.itemId, tRes.updateState);
        await pBasket._fetchEaiwsItems(tUpdateIds, false, true);
        for (const tArticle of tUpdateArticles) {
            const tState = tResultMap.get(tArticle.id);
            if (tState != null)
                tArticle._setOfmlUpdateState(tState, false);
            if (tState === "UpToDate") {
                tResult.updated++;
                await tArticle.updateWholeComposite(ItemUpdateFlags.All & ~ItemUpdateFlags.UserFields);
                pBasket.eventOfmlArticleUpdated.trigger(tArticle);
            }
            else {
                if (tState === "Migratable")
                    tResult.migratable++;
                else
                    tResult.invalid++;
            }
        }
    }
    finally {
        pBasket._endCaching();
        pBasket._endItemUpdate();
    }
    return (tResult);
}
/**
 * Imports the given Obx file.
 * Returns null if the file upload was aborted.
 */
export async function importObx(pBasket, pObxData, pOnUploadProgress) {
    //upload obx
    const tBlob = (pObxData instanceof ArrayBuffer) ? new Blob([pObxData]) : pObxData;
    const tUrl = await pBasket.session.uploadFile("CutBuffer", tBlob, pOnUploadProgress);
    if (isNullOrEmpty(tUrl))
        return (null);
    //paste obx
    const tPastedItems = await pasteItems(pBasket, tUrl);
    //delete uploaded obx
    await pBasket.session.deleteFile(tUrl);
    return (tPastedItems);
}
/**
 * Imports the given pec file.
 * Returns null if the file upload was aborted.
 */
export async function importPec(pBasket, pPecData, pOnUploadProgress) {
    //load zip
    const tZip = new JSZip();
    await tZip.loadAsync(pPecData);
    //read obx
    const tZipFile = tZip.file("document.obx");
    if (tZipFile == null)
        return (new Array()); //obx is optional
    const tObxData = await tZipFile.async("arraybuffer");
    return (importObx(pBasket, tObxData, pOnUploadProgress));
}
export async function cloneItem(pBasket, pItem) {
    if (pItem === pBasket.root)
        throw new Error("Root item can't be cloned.");
    //use copy paste
    const tUrl = await copyItems(pBasket, [pItem], { wholeComposite: true, children: false });
    const tPastedItems = await pasteItems(pBasket, tUrl, { parent: pItem.parent });
    //delete obx after paste
    await pBasket.session.deleteFile(tUrl);
    if (tPastedItems.length > 0)
        return (tPastedItems[0]);
    else
        throw new Error("Failed to clone item.");
}
//// internals /////////////////////////////////////////////////////////////////////////////////////
function getParentsToUpdateAfterRemove(pItems, pRemoveWholeComposite, pRemoveChildren, pRemoveSubArticles = false, pAdditionalParents) {
    //get all items which will be deleted
    const tItemsToDelete = new Set();
    const addItem = (pItem) => {
        tItemsToDelete.add(pItem);
        //add child items
        if (pRemoveChildren) {
            for (const tChild of pItem.children) {
                addItem(tChild);
            }
        }
        //add sub articles
        if (pRemoveWholeComposite && pItem instanceof ArticleItem) {
            const tSubArticles = pItem.getSubArticles(false);
            for (const tSub of tSubArticles) {
                addItem(tSub);
            }
        }
    };
    for (const tItem of pItems) {
        if (tItem instanceof ArticleItem) {
            const tMainArticle = tItem.getMainArticle();
            if (pRemoveWholeComposite) {
                addItem(tMainArticle); //only add main article
            }
            else {
                if (tMainArticle === tItem || pRemoveSubArticles) { //ignore sub articles if pRemoveSubArticles === false
                    addItem(tItem);
                }
            }
        }
        else {
            addItem(tItem);
        }
    }
    //collect parents of all deleted items
    const tParents = new Set();
    tItemsToDelete.forEach((pItem) => {
        //get parent of each item and add it if it will not be deleted itself
        const tParent = pItem.parent;
        if (tParent != null && !tItemsToDelete.has(tParent)) {
            tParents.add(tParent);
        }
        //get set article of each item and add it if it will not be deleted itself
        const tSet = pItem.getSetArticle();
        if (tSet != null && !tItemsToDelete.has(tSet)) {
            tParents.add(tSet);
        }
    });
    //add additional items
    if (pAdditionalParents != null) {
        for (const tParent of pAdditionalParents) {
            tParents.add(tParent);
        }
    }
    return (Array.from(tParents));
}
async function updateParentsAfterInsertOrRemove(pBasket, pParents, pNewItemIds, pWholeComposite) {
    try {
        pBasket.beginSelectionChange();
        pBasket._beginItemUpdate();
        pBasket._beginCaching();
        const tUpdatePromises = new Array();
        //fetch parents
        const tParentIds = pParents.map((pParent) => pParent.id);
        if (pNewItemIds != null)
            tParentIds.push(...pNewItemIds);
        await pBasket._fetchEaiwsItems(tParentIds, false, pWholeComposite === true);
        //update parents and invalidate group calculations
        for (const tParent of pParents) {
            if (tParent instanceof SetArticleItem)
                tUpdatePromises.push(tParent.updateItem(ItemUpdateFlags.Item | ItemUpdateFlags.ItemProps));
            else
                tUpdatePromises.push(tParent.updateItem(ItemUpdateFlags.Item));
            tParent.invalidateGroupCalculations();
        }
        await Promise.all(tUpdatePromises);
    }
    finally {
        pBasket._endCaching();
        pBasket._endItemUpdate();
        pBasket.endSelectionChange();
    }
}
function getInsertPosition(pBasket, pParent, pBeforeItem, pNoSetParents) {
    if (pParent != null) {
        return ({
            parent: pParent,
            beforeItemId: pBeforeItem != null ? pBeforeItem.id : null
        });
    }
    //insert position based on selection
    if (pBasket.selection.length < 1) {
        return ({
            parent: pBasket.root,
            beforeItemId: null
        });
    }
    //get last selected item
    let tSelectedItem;
    if (pBasket.selection.length === 1)
        tSelectedItem = pBasket.selection[0];
    else {
        //sort the selection to get the last item
        const tSortedSelection = sortItems(pBasket.selection);
        tSelectedItem = tSortedSelection[tSortedSelection.length - 1];
    }
    //get parent (folder or set)
    let tParent = null;
    if (tSelectedItem.isGroup && pBasket.selection.length === 1) {
        //use selected group as parent for single selection
        tParent = tSelectedItem;
    }
    else
        tParent = tSelectedItem.getParentGroup();
    //ignore set article parents (or folders which are part of a set)
    if (tParent != null && pNoSetParents) {
        while (tParent != null && (tParent instanceof SetArticleItem || tParent.getSetArticle() != null)) {
            tParent = tParent.getParentGroup();
        }
    }
    if (tParent == null)
        tParent = pBasket.root;
    //insert after current selection
    const tBeforeItem = tParent.getNextChild(tSelectedItem);
    return ({
        parent: tParent,
        beforeItemId: tBeforeItem != null ? tBeforeItem.id : null
    });
}
function sortItems(pItems) {
    if (pItems.length < 2)
        return (clone(pItems));
    const tTempItems = pItems.map((pItem) => ({ item: pItem, no: pItem._getItemNumberAsArray() }));
    tTempItems.sort((a, b) => {
        //find first numbers which are not equal
        let tA;
        let tB;
        for (let i = 0; i < a.no.length; ++i) {
            tA = a.no[i];
            tB = b.no[i];
            if (tA !== tB) {
                break;
            }
        }
        //found two equal numbers (or two times undefined), is b longer than a?
        //in this case we have to sort a before b, otherwise a and b are equal
        if (tA === tB) {
            return (a.no.length < b.no.length ? -1 : 0);
        }
        //a or b is undefined, if b is undefined it could be shorther than a in this case?
        if (tA === undefined || tB === undefined) {
            return (a.no.length - b.no.length);
        }
        return (tA - tB);
    });
    const tSortedItems = tTempItems.map((pEntry) => pEntry.item);
    return (tSortedItems);
}
/** Note: the set itself will not be updated here */
async function addItemsToCurrentSet(pBasket, pItems) {
    if (pItems.length === 0)
        return;
    try {
        pBasket._beginItemUpdate();
        pBasket._beginCaching();
        //collect items to add
        //Note: OperationMode.subPositions is not supported from addToSetArticle
        const tItemsToAdd = collectAllItems(pItems);
        const tOpMode = new basket.OperationMode();
        tOpMode.breakUpSetArticles = false;
        const tItemsToAddIds = tItemsToAdd.map((pItem) => pItem.id);
        await pBasket.session.basket.addToSetArticle(tItemsToAddIds, tOpMode);
        await pBasket._fetchEaiwsItems(tItemsToAddIds, true, false);
        const tPromises = new Array();
        for (const tItem of tItemsToAdd) {
            tPromises.push(tItem.updateItem(ItemUpdateFlags.Item | ItemUpdateFlags.IncludeChildren));
        }
        await Promise.all(tPromises);
    }
    catch (pError) {
        Log.error("Failed to add items to set.", pError);
    }
    finally {
        pBasket._endCaching();
        pBasket._endItemUpdate();
    }
}
/** Note: the set itself will not be updated here */
async function removeItemsFromCurrentSet(pBasket, pItems, pUpdateItems) {
    if (pItems.length === 0)
        return;
    //collect all set parts
    //Note: OperationMode.subPositions is not supported from removeFromSetArticle
    //we have to collect the children here
    const tSetParts = new Set();
    const collectItems = (pItem) => {
        //ignore sets, to allow nested set articles
        if (pItem.type === "SetArticle")
            return;
        const tSet = pItem.getSetArticle();
        if (tSet != null) {
            tSetParts.add(pItem);
        }
        for (const tChild of pItem.children) {
            collectItems(tChild);
        }
    };
    for (const tItem of pItems) {
        collectItems(tItem);
    }
    if (tSetParts.size === 0)
        return;
    try {
        pBasket._beginItemUpdate();
        pBasket._beginCaching();
        const tSetPartsArray = Array.from(tSetParts);
        const tPartIds = tSetPartsArray.map((pPart) => pPart.id);
        await pBasket.session.basket.removeFromSetArticle(tPartIds);
        if (pUpdateItems) {
            await pBasket._fetchEaiwsItems(tPartIds, true, false);
            const tPromises = new Array();
            for (const tPart of tSetPartsArray) {
                tPromises.push(tPart.updateItem(ItemUpdateFlags.Item | ItemUpdateFlags.IncludeChildren));
            }
            await Promise.all(tPromises);
        }
    }
    catch (pError) {
        Log.error("Failed to remove items from set.", pError);
    }
    finally {
        pBasket._endCaching();
        pBasket._endItemUpdate();
    }
}
//collects all items including its children
function collectAllItems(pItems) {
    const tCollected = new Set();
    const collectItems = (pItem) => {
        tCollected.add(pItem);
        for (const tChild of pItem.children) {
            collectItems(tChild);
        }
    };
    for (const tItem of pItems) {
        collectItems(tItem);
    }
    return (Array.from(tCollected));
}
